import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-background-50/80 backdrop-blur-md border-b border-background-200/60">
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 group" onClick={() => setMobileOpen(false)}>
              <span className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 tracking-tight">
                Hipobuy <span className="text-primary-500">Sheet</span>
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-6">
              <Link
                to="/hipobuy-sheet-guide"
                className="text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Complete Guide
              </Link>
              <Link
                to="/hipobuy-sheet-safety"
                className="text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Safety Center
              </Link>
              <Link
                to="/hipobuy-sheet-comparison"
                className="text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Comparison
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Visit Main Site
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-5 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                Buy It Now
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-xs"></i>
                </span>
              </a>
            </div>

            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
              aria-label="Open menu"
            >
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile overlay — guaranteed fullscreen with fixed inset-0 container */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[9999] md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileOpen(false)}
          />
          {/* Panel */}
          <div className="absolute top-0 right-0 bottom-0 w-[85vw] max-w-[340px] bg-background-50 flex flex-col shadow-2xl">
            {/* Panel header */}
            <div className="flex items-center justify-between px-5 h-16 border-b border-background-200/60 shrink-0">
              <span className="text-lg font-heading font-semibold text-foreground-950">
                Hipobuy <span className="text-primary-500">Sheet</span>
              </span>
              <button
                onClick={() => setMobileOpen(false)}
                className="w-9 h-9 flex items-center justify-center text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer"
                aria-label="Close menu"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto">
              {/* Nav links */}
              <div className="px-3 py-4 border-b border-background-200/60">
                <h4 className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider mb-2 px-2">Navigation</h4>
                <div className="space-y-0.5">
                  <Link
                    to="/"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                      <i className="ri-home-line text-sm"></i>
                    </span>
                    Home
                  </Link>
                  <Link
                    to="/hipobuy-sheet-guide"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                      <i className="ri-book-open-line text-sm"></i>
                    </span>
                    Complete Guide
                  </Link>
                  <Link
                    to="/hipobuy-sheet-safety"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                      <i className="ri-shield-check-line text-sm"></i>
                    </span>
                    Safety Center
                  </Link>
                  <Link
                    to="/hipobuy-sheet-comparison"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                      <i className="ri-scales-3-line text-sm"></i>
                    </span>
                    Comparison
                  </Link>
                </div>
              </div>

              {/* Categories */}
              <div className="px-3 py-4">
                <h4 className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider mb-2 px-2">Product Categories</h4>
                <div className="space-y-0.5">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                    >
                      <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                        <i className={`${cat.icon} text-sm`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* CTA buttons */}
            <div className="px-4 py-4 border-t border-background-200/60 space-y-2.5 shrink-0">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                Hipobuy Sheet
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-external-link-line text-xs"></i>
                </span>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-foreground-900 text-background-50 text-sm font-semibold rounded-full hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer"
              >
                Buy It Now
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-xs"></i>
                </span>
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}